/**
 * PDF Viewer Controls Component
 *
 * Provides zoom and page navigation controls for the PDF viewer
 */

import React, { useState, useEffect, useRef } from 'react';
import styles from './ViewerControls.module.scss';
import { ReactComponent as SidebarCollapseIcon } from '../../shared/icons/sidebarcollapse.svg';
import { ReactComponent as SidebarExpandIcon } from '../../shared/icons/sidebarexpand.svg';

export interface ViewerControlsProps {
  currentPage: number;
  totalPages: number;
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetZoom: () => void;
  onPageChange: (page: number) => void;
  // Search props
  onSearch?: (query: string) => void;
  searchResultsCount?: number;
  currentSearchResult?: number;
  onNextSearchResult?: () => void;
  onPreviousSearchResult?: () => void;
  // Thumbnail toggle props
  thumbnailsVisible?: boolean;
  onToggleThumbnails?: () => void;
  showThumbnailToggle?: boolean;
}

export const ViewerControls: React.FC<ViewerControlsProps> = ({
  currentPage,
  totalPages,
  zoom,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onPageChange,
  onSearch,
  searchResultsCount = 0,
  currentSearchResult = 0,
  onNextSearchResult,
  onPreviousSearchResult,
  thumbnailsVisible = false,
  onToggleThumbnails,
  showThumbnailToggle = false,
}) => {
  const zoomPercentage = Math.round(zoom * 100);
  const [searchQuery, setSearchQuery] = useState('');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced search: wait 400ms after user stops typing
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      if (onSearch) {
        onSearch(searchQuery);
      }
    }, 400);

    // Cleanup on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, onSearch]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  return (
    <div className={styles.controls}>
      {/* Thumbnail Toggle */}
      {showThumbnailToggle && (
        <button
          className={styles.thumbnailToggle}
          onClick={onToggleThumbnails}
          title={thumbnailsVisible ? 'Collapse Sidebar' : 'Expand Sidebar'}
        >
          {thumbnailsVisible ? (
            <SidebarCollapseIcon width={18} height={18} />
          ) : (
            <SidebarExpandIcon width={18} height={18} />
          )}
        </button>
      )}

      {/* Page Navigation */}
      <div className={styles.pageControls}>
        <span className={styles.pageInfo}>
          <input
            type="number"
            className={styles.pageInput}
            value={currentPage}
            min={1}
            max={totalPages}
            onChange={(e) => {
              const page = parseInt(e.target.value, 10);
              if (!isNaN(page)) {
                onPageChange(page);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const page = parseInt((e.target as HTMLInputElement).value, 10);
                if (!isNaN(page) && page >= 1 && page <= totalPages) {
                  onPageChange(page);
                  (e.target as HTMLInputElement).blur(); // Remove focus after navigation
                }
              }
            }}
          />
          <span className={styles.pageSeparator}>/</span>
          <span className={styles.totalPages}>{totalPages}</span>
        </span>
      </div>

      {/* Separator */}
      <div className={styles.separator}></div>

      {/* Zoom Controls */}
      <div className={styles.zoomControls}>
        <button
          className={styles.controlButton}
          onClick={onZoomOut}
          title="Zoom Out"
        >
          −
        </button>
        <button
          className={styles.zoomButton}
          onClick={onResetZoom}
          title="Reset Zoom (100%)"
        >
          {zoomPercentage}%
        </button>
        <button
          className={styles.controlButton}
          onClick={onZoomIn}
          title="Zoom In"
        >
          +
        </button>
      </div>

      {/* Separator */}
      {onSearch && <div className={styles.separator}></div>}

      {/* Search Controls */}
      {onSearch && (
        <div className={styles.searchControls}>
          <div className={styles.searchInputWrapper}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search in document..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className={styles.clearButton}
                onClick={handleClearSearch}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          <div className={styles.searchResultsContainer}>
            {searchResultsCount > 0 && (
              <>
                <span className={styles.searchCount}>
                  {currentSearchResult}/{searchResultsCount}
                </span>
                <button
                  type="button"
                  className={styles.searchNavButton}
                  onClick={onPreviousSearchResult}
                  disabled={searchResultsCount === 0}
                  title="Previous result"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className={styles.searchNavButton}
                  onClick={onNextSearchResult}
                  disabled={searchResultsCount === 0}
                  title="Next result"
                >
                  ›
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
